import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import TitleDescription from "../../../../components/newMotorPolicy/titleDescription/titleDescription";
import PolicyCard from "../../../../components/policyCard/policyCard";
import useLanguageTranslate from "../../../../hooks/useLanguageTranslate";
import usePolicyCancellationFlow from "../../../../hooks/usePolicyCancellationFlow";
import {
  getPolicyViewHeaderPartyResponse,
  getPolicyViewHeaderResponse,
} from "../../../../redux/features/policyDetailsSlice";
import { getClientSource } from "../../../../utils/getClientSource";
import BottomLayout from "../../../motorInsurancePages/policyOverview/managePolicy/policyCancellationFlow/bottomLayout";
import PolicyCancellationMotorLayout from "../../../motorInsurancePages/policyOverview/managePolicy/policyCancellationFlow/policyCancellationMotorLayout";
import AlertComponent from "../../../motorInsurancePages/transferPolicyFlow/alertComponent";

const HomeInsuranceCancellationReviewPage = ({
  handleNavigationToPolicyOverview,
}) => {
  const translate = useLanguageTranslate();
  const navigate = useNavigate();
  const { handleNextSubstep } = usePolicyCancellationFlow();

  const { t } = useTranslation();

  // Get policy detail from Redux
  const policyDetailsInfo = useSelector(getPolicyViewHeaderResponse);
  const policyDetailsPartyInfo = useSelector(getPolicyViewHeaderPartyResponse);

  // DETAILED LOGGING FOR BACKEND TEAM
  console.log("=== HOME INSURANCE POLICY CANCEL DATA DEBUG ===");
  console.log(
    "Full policyDetailsInfo:",
    JSON.stringify(policyDetailsInfo, null, 2)
  );
  console.log(
    "Full policyDetailsPartyInfo:",
    JSON.stringify(policyDetailsPartyInfo, null, 2)
  );
  console.log(
    "Property Address:",
    policyDetailsInfo?.PropertyAddress || policyDetailsInfo?.Address
  );
  console.log(
    "Property Type:",
    policyDetailsInfo?.PropertyType || policyDetailsInfo?.BuildingType
  );
  console.log(
    "Building Sum Insured:",
    policyDetailsInfo?.BuildingSumInsured || policyDetailsInfo?.BuildingValue
  );
  console.log(
    "Contents Sum Insured:",
    policyDetailsInfo?.ContentsSumInsured || policyDetailsInfo?.ContentsValue
  );
  console.log(
    "Coverage Amount:",
    policyDetailsInfo?.CoverageAmount || policyDetailsInfo?.SumInsured
  );
  console.log("FromDateStr:", policyDetailsInfo?.FromDateStr);
  console.log("ToDateStr:", policyDetailsInfo?.ToDateStr);
  console.log("PolicyNumber:", policyDetailsInfo?.PolicyNumber);
  console.log("=== END DEBUG ===");

  // Helper function to format dates as "10 July 2025"
  const formatDateAsText = (dateStr) => {
    if (!dateStr) return "N/A";

    try {
      let date;

      // Handle different date formats
      if (typeof dateStr === "string" && dateStr.includes("/")) {
        const parts = dateStr.split("/");
        if (parts.length === 3) {
          // Assume DD/MM/YYYY or MM/DD/YYYY format - try both
          date = new Date(parts[2], parts[1] - 1, parts[0]); // DD/MM/YYYY
          if (isNaN(date.getTime())) {
            date = new Date(parts[2], parts[0] - 1, parts[1]); // MM/DD/YYYY
          }
        }
      } else if (typeof dateStr === "string" && dateStr.includes("-")) {
        date = new Date(dateStr);
      } else {
        date = new Date(dateStr);
      }

      if (isNaN(date.getTime())) {
        return dateStr; // Return original if parsing fails
      }

      // Format as "10 July 2025"
      const months = [
        "January",
        "February",
        "March",
        "April",
        "May",
        "June",
        "July",
        "August",
        "September",
        "October",
        "November",
        "December",
      ];

      const day = date.getDate();
      const month = months[date.getMonth()];
      const year = date.getFullYear();

      return `${day} ${month} ${year}`;
    } catch (error) {
      console.error("Date formatting error:", error);
      return dateStr; // Return original string if parsing fails
    }
  };

  // Helper function to format coverage amount
  const formatCoverageAmount = (amount) => {
    if (!amount) return translate("N/A", "غير متوفر");

    const num = typeof amount === "number" ? amount : parseFloat(amount);
    if (!isNaN(num)) {
      return `BHD ${num.toLocaleString("en-US", { minimumFractionDigits: 3, maximumFractionDigits: 3 })}`;
    }

    return amount;
  };

  // Create home insurance policy card info
  const policyCardInfo = {
    ModelYear: "",
    Make: "Home Policy",
    Model: `Block No ${policyDetailsInfo?.Block || "N/A"}, ${
      policyDetailsInfo?.City || "N/A"
    }`,
    CPR: "",
    PolicyNumber: policyDetailsInfo?.PolicyNumber || "--",
    RegistrationNumber: "",
    SumInsured: policyDetailsInfo?.SumInsured || 0.0,
    PlanName: policyDetailsInfo?.PlanName || "--",
    ProductCode: "0710", // Home Insurance product code
    ProductName: "Home Policy",
    StartDate:
      policyDetailsInfo?.FromDateStr?.split(" ")[0]
        ?.split("/")
        ?.reverse()
        ?.join("-") || "--",
    EndDate:
      policyDetailsInfo?.ToDateStr?.split(" ")[0]
        ?.split("/")
        ?.reverse()
        ?.join("-") || "--",
    FromDateStr: policyDetailsInfo?.FromDateStr?.split(" ")[0] || "--",
    ToDateStr: policyDetailsInfo?.ToDateStr?.split(" ")[0] || "--",
    Branch: getClientSource(),
    MainChannel: "Direct Bahrain",
    Renewed: policyDetailsInfo?.IsRenewed,
    PolicyStatus: "Active",
    IsRenewable: false,
  };

  const continueClickHandler = () => {
    handleNextSubstep();
  };

  return (
    <PolicyCancellationMotorLayout
      is_hide_back_btn={false}
      show_close_btn={true}
      handleCloseBtnClick={handleNavigationToPolicyOverview}
      btn_onClick={continueClickHandler}
      btn_disabled={false}
      btn_label={translate("Continue", "التالي")}
      CancellationAdditionalComponent={
        <BottomLayout
          className="btvk"
          btn_onClick={continueClickHandler}
          btn_disabled={false}
          btn_label={translate("Continue", "التالي")}
          handleNavigationToPolicyOverview={handleNavigationToPolicyOverview}
        />
      }
    >
      <div className="flex flex-col items-center mx-auto max-w-lg pb-20 max767:p-5 max767:mt-0 max767:pt-0 max767:pb-[50px]">
        <div className="flex justify-center items-center w-full">
          <div className=" p-0 w-full ">
            <TitleDescription
              className="max767:mt-0!important"
              title={translate("Policy Cancellation", "تسليم سياستك")}
              description={translate(
                "Please review your home insurance policy details before proceeding with cancellation.",
                "قم بمراجعة تفاصيل سياستك وقم بتوفير المعلومات اللازمة للمضي قدمًا في عملية الاستسلام."
              )}
            />
            <AlertComponent
              className="mb-4"
              title={translate(
                "Note that cancelling your policy is a request and is subject to approval based on our cancellation policy. Ensure you understand the implications of this action.",
                "يرجى ملاحظة أن هذا الطلب سيتم تقديمه للموافقة عليه قبل أن يصبح ساري المفعول"
              )}
            />
            <div className="flex flex-col justify-center items-center mt-5 bg-[#F6F8F9] p-4 rounded-2xl max767:bg-white max767:p-0">
              <div className="relative">
                <PolicyCard
                  className="mb-4"
                  policy_bg_image={policyCardInfo.policy_bg_image}
                  expire_days={policyCardInfo.expire_days}
                  is_text_dark={policyCardInfo.is_text_dark}
                  mapping_key={policyCardInfo.id}
                  data={policyCardInfo}
                  disableCardClick
                />
                {/* Custom Active Badge */}
                <div className="absolute top-4 left-4 rtl:right-4 rtl:left-auto">
                  <span className="text-[#00719A] text-xs not-italic font-normal leading-4 rounded bg-[#E3F8FF] px-1 py-0">
                    {translate("Active", "ساري")}
                  </span>
                </div>
              </div>

              {/* Home Insurance specific table structure - same on mobile and desktop */}
              <table className="min-w-full border-separate bg-white border border-solid border-[#DDE2E4] border-spacing-0 rounded-[10px]">
                <tbody>
                  {/* 1. House Number */}
                  <tr>
                    <td className="py-2 px-4 border-b border-r rtl:border-l text-[#5B6871] text-sm font-medium leading-6 h-[44px]">
                      {translate("Building Number", "رقم المبنى")}
                    </td>
                    <td className="py-0 px-4 border-b text-[#1A2024] text-sm font-medium leading-6">
                      {policyDetailsInfo?.Building ||
                        translate("N/A", "غير متوفر")}
                    </td>
                  </tr>

                  {/* 2. Road Number */}
                  <tr>
                    <td className="py-2 px-4 border-b border-r rtl:border-l text-[#5B6871] text-sm font-medium leading-6 h-[44px]">
                      {translate("Road Number", "رقم الشارع")}
                    </td>
                    <td className="py-0 px-4 border-b text-[#1A2024] text-sm font-medium leading-6">
                      {policyDetailsInfo?.Road || translate("N/A", "غير متوفر")}
                    </td>
                  </tr>

                  {/* 3. Block Number */}
                  <tr>
                    <td className="py-2 px-4 border-b border-r rtl:border-l text-[#5B6871] text-sm font-medium leading-6 h-[44px]">
                      {translate("Block Number", "رقم المربع")}
                    </td>
                    <td className="py-0 px-4 border-b text-[#1A2024] text-sm font-medium leading-6">
                      {policyDetailsInfo?.Block ||
                        translate("N/A", "غير متوفر")}
                    </td>
                  </tr>

                  {/* 4. City */}
                  {/* <tr>
                    <td className="py-2 px-4 border-b border-r rtl:border-l text-[#5B6871] text-sm font-medium leading-6 h-[44px]">
                      {translate("City", "المدينة")}
                    </td>
                    <td className="py-0 px-4 border-b text-[#1A2024] text-sm font-medium leading-6">
                      {policyDetailsInfo?.City || translate("N/A", "غير متوفر")}
                    </td>
                  </tr> */}

                  {/* 5. Building Age */}
                  <tr>
                    <td className="py-2 px-4 border-b border-r rtl:border-l text-[#5B6871] text-sm font-medium leading-6 h-[44px]">
                      {translate("Building Age", "عمر المبنى")}
                    </td>
                    <td className="py-0 px-4 border-b text-[#1A2024] text-sm font-medium leading-6">
                      {policyDetailsInfo?.Age
                        ? `${policyDetailsInfo.Age} ${translate(
                            "years",
                            "سنة"
                          )}`
                        : translate("N/A", "غير متوفر")}
                    </td>
                  </tr>

                  {/* 6. Total Sum Insured */}
                  <tr>
                    <td className="py-2 px-4 border-b border-r rtl:border-l text-[#5B6871] text-sm font-medium leading-6 h-[44px]">
                      {translate("Total Sum Insured", "المبلغ المؤمن عليه")}
                    </td>
                    <td className="py-0 px-4 border-b text-[#1A2024] text-sm font-medium leading-6">
                      {formatCoverageAmount(policyDetailsInfo?.SumInsured)}
                    </td>
                  </tr>

                  {/* 7. Add-ons */}
                  {(() => {
                    // Get add-ons from policy details Covers - using the same logic as summary page
                    const addons = [];
                    const coversData = policyDetailsInfo?.Covers || {};

                    // All Risk (CPRO21) - check both old and new keys
                    if (
                      coversData?.allRisk ||
                      coversData?.cpro21 ||
                      coversData?.AllRisk
                    ) {
                      addons.push(translate("All Risk", "جميع المخاطر"));
                    }

                    // Domestic Helper (CPRO35) - check both old and new keys
                    if (
                      coversData?.domesticHelper ||
                      coversData?.cpro35 ||
                      coversData?.DomesticHelper
                    ) {
                      addons.push(
                        translate("Domestic Helper", "العامل المنزلي")
                      );
                    }

                    // VIP Cover (CPRO13) - check both old and new keys
                    if (
                      coversData?.vipCover ||
                      coversData?.cpro13 ||
                      coversData?.VIPCover ||
                      coversData?.VIP
                    ) {
                      addons.push(translate("VIP Cover", "تغطية كبار الشخصيات"));
                    }

                    // Terrorism (CPRO33) - check both old and new keys
                    if (
                      coversData?.sabotageAndTerrorism ||
                      coversData?.cpro33 ||
                      coversData?.Terrorism ||
                      coversData?.TerrorismCover
                    ) {
                      addons.push(
                        translate("Terrorism Attack", "التخريب والإرهاب")
                      );
                    }

                    return addons.length > 0 ? (
                      <tr>
                        <td className="py-2 px-4 border-b border-r rtl:border-l text-[#5B6871] text-sm font-medium leading-6 h-[44px]">
                          {translate("Add-ons", "الإضافات")}
                        </td>
                        <td className="py-0 px-4 border-b text-[#1A2024] text-sm font-medium leading-6">
                          {addons.map((addon, index) => (
                            <div key={index}>{addon}</div>
                          ))}
                        </td>
                      </tr>
                    ) : null;
                  })()}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </PolicyCancellationMotorLayout>
  );
};

export default HomeInsuranceCancellationReviewPage;
